/**
 * Dynamic Dimension Helper
 * Calculates real-world dimensions based on a reference door in the project
 */

// Standard door dimensions in inches
const STANDARD_DOOR = {
  width: 36,  // inches
  height: 82.5 // inches
};

/**
 * Find the reference item in the canvas (doors first, then any manually set reference)
 * Returns the item that serves as calibration reference
 */
export const findReferenceItem = (canvas) => {
  if (!canvas) return null;
  
  const objects = canvas.getObjects();
  
  // First, check for manually set reference items
  const manualReference = objects.find(obj => obj.isReferenceItem === true);
  if (manualReference) {
    return manualReference;
  }
  
  // Then look for doors (legacy behavior)
  const doors = objects.filter(obj => obj.itemType === 'doors');
  
  // First try to find a door with valid real dimensions
  const doorsWithDimensions = doors.filter(door => 
    door.realWidth && 
    door.realHeight && 
    door.realWidth !== 'N/A' && 
    door.realHeight !== 'N/A' &&
    !isNaN(parseFloat(door.realWidth)) &&
    !isNaN(parseFloat(door.realHeight))
  );
  
  if (doorsWithDimensions.length > 0) {
    return doorsWithDimensions[0];
  }
  
  // If no door has dimensions yet, return the first door (it will get dimensions from AI or standard)
  return doors.length > 0 ? doors[0] : null;
};

/**
 * Legacy function name for backward compatibility
 */
export const findReferenceDoor = findReferenceItem;

/**
 * Set an item as the reference item
 */
export const setReferenceItem = (canvas, targetItem, realWidth, realHeight) => {
  if (!canvas || !targetItem) return false;
  
  const objects = canvas.getObjects();
  
  // Clear any existing reference items
  objects.forEach(obj => {
    if (obj.isReferenceItem) {
      delete obj.isReferenceItem;
    }
  });
  
  // Set the new reference item
  targetItem.isReferenceItem = true;
  targetItem.realWidth = realWidth.toString();
  targetItem.realHeight = realHeight.toString();
  
  // Update all dimensions based on new reference
  updateAllDimensions(canvas);
  
  return true;
};

/**
 * Clear reference item status from all items
 */
export const clearReferenceItem = (canvas) => {
  if (!canvas) return false;
  
  const objects = canvas.getObjects();
  objects.forEach(obj => {
    if (obj.isReferenceItem) {
      delete obj.isReferenceItem;
    }
  });
  
  canvas.renderAll();
  return true;
};

/**
 * Check if an item is the current reference item
 */
export const isReferenceItem = (item) => {
  return item && item.isReferenceItem === true;
};

/**
 * Convert inches to centimeters
 */
export const inchesToCm = (inches) => {
  return (parseFloat(inches) * 2.54).toFixed(1);
};

/**
 * Convert centimeters to inches
 */
export const cmToInches = (cm) => {
  return (parseFloat(cm) / 2.54).toFixed(1);
};

/**
 * Calculate current scale factor based on reference item
 * Returns pixels per inch for both width and height
 */
export const calculateCurrentScaleFactor = (referenceItem) => {
  if (!referenceItem) return null;
  
  const currentPixelWidth = referenceItem.width * referenceItem.scaleX;
  const currentPixelHeight = referenceItem.height * referenceItem.scaleY;
  
  // If reference item doesn't have real dimensions yet, assume it's a standard door
  let referenceRealWidth = STANDARD_DOOR.width;
  let referenceRealHeight = STANDARD_DOOR.height;
  
  if (referenceItem.realWidth && referenceItem.realHeight && 
      referenceItem.realWidth !== 'N/A' && referenceItem.realHeight !== 'N/A') {
    const parsedWidth = parseFloat(referenceItem.realWidth);
    const parsedHeight = parseFloat(referenceItem.realHeight);
    
    if (!isNaN(parsedWidth) && !isNaN(parsedHeight)) {
      referenceRealWidth = parsedWidth;
      referenceRealHeight = parsedHeight;
    }
  }
  
  // How many pixels per inch based on reference item dimensions
  return {
    pixelsPerInchX: currentPixelWidth / referenceRealWidth,
    pixelsPerInchY: currentPixelHeight / referenceRealHeight,
    referenceItemId: referenceItem.id || referenceItem.groupId,
    referenceItem: referenceItem
  };
};

/**
 * Calculate real dimensions for any object based on current scale
 */
export const calculateRealDimensions = (object, scaleFactor) => {
  if (!object || !scaleFactor) return { width: 'N/A', height: 'N/A' };
  
  const currentPixelWidth = object.width * object.scaleX;
  const currentPixelHeight = object.height * object.scaleY;
  
  const realWidth = currentPixelWidth / scaleFactor.pixelsPerInchX;
  const realHeight = currentPixelHeight / scaleFactor.pixelsPerInchY;
  
  return {
    width: realWidth.toFixed(1),
    height: realHeight.toFixed(1)
  };
};

/**
 * Calculate dimensions for a new item based on its default size and current scale
 */
export const calculateNewItemDimensions = (defaultPixelSize, scaleFactor) => {
  if (!scaleFactor) return { width: 'N/A', height: 'N/A' };
  
  const realWidth = defaultPixelSize.width / scaleFactor.pixelsPerInchX;
  const realHeight = defaultPixelSize.height / scaleFactor.pixelsPerInchY;
  
  return {
    width: realWidth.toFixed(1),
    height: realHeight.toFixed(1)
  };
};

/**
 * Update real dimensions for all objects on canvas
 */
export const updateAllDimensions = (canvas) => {
  if (!canvas) return;
  
  const referenceItem = findReferenceItem(canvas);
  if (!referenceItem) {
    return;
  }
  
  const scaleFactor = calculateCurrentScaleFactor(referenceItem);
  if (!scaleFactor) return;
  
  const objects = canvas.getObjects();
  let updatedCount = 0;
  
  objects.forEach(obj => {
    if (obj.itemType === 'doors' || obj.itemType === 'window') {
      const realDimensions = calculateRealDimensions(obj, scaleFactor);
      
      // Update the object's real dimensions
      obj.realWidth = realDimensions.width;
      obj.realHeight = realDimensions.height;
      
      updatedCount++;
    }
  });
  
  canvas.renderAll();
  
  return {
    scaleFactor,
    updatedCount,
    referenceItemId: referenceItem.id || referenceItem.groupId
  };
};

/**
 * Get dimension info for contextual menu display
 */
export const getDimensionInfo = (object, canvas) => {
  const referenceItem = findReferenceItem(canvas);
  if (!referenceItem) {
    return {
      realWidth: object.realWidth || 'N/A',
      realHeight: object.realHeight || 'N/A',
      isCalculated: false,
      isReference: false,
      canSetAsReference: true
    };
  }
  
  const isRef = isReferenceItem(object);
  const scaleFactor = calculateCurrentScaleFactor(referenceItem);
  if (!scaleFactor) {
    return {
      realWidth: object.realWidth || 'N/A',
      realHeight: object.realHeight || 'N/A',
      isCalculated: false,
      isReference: isRef,
      canSetAsReference: !isRef
    };
  }
  
  const realDimensions = calculateRealDimensions(object, scaleFactor);
  return {
    realWidth: realDimensions.width,
    realHeight: realDimensions.height,
    realWidthCm: inchesToCm(realDimensions.width),
    realHeightCm: inchesToCm(realDimensions.height),
    isCalculated: true,
    isReference: isRef,
    canSetAsReference: !isRef,
    scaleFactor: scaleFactor
  };
};

export default {
  findReferenceItem,
  findReferenceDoor, // Legacy support
  calculateCurrentScaleFactor,
  calculateRealDimensions,
  calculateNewItemDimensions,
  updateAllDimensions,
  getDimensionInfo,
  setReferenceItem,
  clearReferenceItem,
  isReferenceItem,
  inchesToCm,
  cmToInches,
  STANDARD_DOOR
};
